import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
 Card,
 CardContent,
 CardDescription,
 CardHeader,
 CardTitle,
} from '@/components/ui/card';
import { useAuth } from '@/providers/AuthProvider';

interface EmailVerificationProps {
 email: string;
 onSuccess?: () => void;
}

export function EmailVerification({
 email,
 onSuccess,
}: EmailVerificationProps) {
 const [code, setCode] = useState('');
 const [isLoading, setIsLoading] = useState(false);
 const [isResending, setIsResending] = useState(false);
 const { verifyEmail, resendVerificationCode } = useAuth();

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!code.trim()) return;

  setIsLoading(true);
  try {
   await verifyEmail(email, code);
   onSuccess?.();
  } catch (error) {
   // Error is handled by AuthProvider
  } finally {
   setIsLoading(false);
  }
 };

 const handleResend = async () => {
  setIsResending(true);
  try {
   await resendVerificationCode(email);
  } catch (error) {
   // Error is handled by AuthProvider
  } finally {
   setIsResending(false);
  }
 };

 return (
  <Card className='w-full max-w-md'>
   <CardHeader className='text-center'>
    <CardTitle>Verify Your Email</CardTitle>
    <CardDescription>
     We've sent a 6-digit verification code to <strong>{email}</strong>
    </CardDescription>
   </CardHeader>
   <CardContent>
    <form onSubmit={handleSubmit} className='space-y-4'>
     <div className='space-y-2'>
      <Label htmlFor='code'>Verification Code</Label>
      <Input
       id='code'
       type='text'
       placeholder='Enter 6-digit code'
       value={code}
       onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
       maxLength={6}
       className='text-center text-lg tracking-widest'
       required
      />
     </div>

     <Button
      type='submit'
      className='w-full'
      disabled={isLoading || code.length !== 6}
     >
      {isLoading ? (
       <>
        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
        Verifying...
       </>
      ) : (
       'Verify Email'
      )}
     </Button>

     <div className='text-center'>
      <p className='text-sm text-muted-foreground mb-2'>
       Didn't receive the code?
      </p>
      <Button
       type='button'
       variant='ghost'
       onClick={handleResend}
       disabled={isResending}
       className='text-sm'
      >
       {isResending ? 'Sending...' : 'Resend Code'}
      </Button>
     </div>
    </form>
   </CardContent>
  </Card>
 );
}
